import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  REPORT_PERIOD_LIMIT_DAYS,
  type CollectionsReportView,
  type ReportPeriod,
} from '@wodi/shared';
import { Button, PageHeader, PrintTable } from '@wodi/ui';
import { useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { fetchCollections, reportKeys } from '../api/reports.ts';
import { sessionQuery } from '../api/session.ts';
import { METHOD_LABELS, money } from '../billing/format.ts';
import { formatInstant } from '../patients/format.ts';
import {
  AsAt,
  BackToReports,
  facilityWords,
  FacilityFilter,
  PeriodFields,
  periodIsValid,
  periodWords,
  PrintableReport,
  ReportActions,
  ReportStates,
  useFacilityChoice,
  useSheet,
  yesterdayIso,
} from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';

type Facility = CollectionsReportView['facilities'][number];
type Totals = Facility['total'];

const RECONCILIATION_WORDS = {
  open: 'Shift still open',
  reconciles: 'Matches the cash-up',
  differs: 'Differs from the cash-up',
} as const;

/**
 * Daily collections (plan §2.5; P4, P15, P16): money in by method, cashier and shift for
 * an EAT day or range, refunds on their own line, and payments entered after the day ended
 * listed separately so a reprint that differs shows why.
 */
export function CollectionsReportPage({ day }: { day?: string | undefined }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const choice = useFacilityChoice('daily-collections');
  const start = day ?? yesterdayIso();
  const [period, setPeriod] = useState<ReportPeriod>({ from: start, to: start });
  const [facility, setFacility] = useState(session?.facility.id ?? '');
  const [shown, setShown] = useState({ period, facilityIds: choice.idsFor(facility) });
  const report = useQuery({
    queryKey: reportKeys.report('daily-collections', shown),
    queryFn: () => fetchCollections(shown),
  });
  const sheet = useSheet();
  const valid = periodIsValid(period, REPORT_PERIOD_LIMIT_DAYS.money);
  const data = report.data;
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader title="Daily collections" />
      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) setShown({ period, facilityIds: choice.idsFor(facility) });
        }}
      >
        <PeriodFields
          period={period}
          onChange={setPeriod}
          maxDays={REPORT_PERIOD_LIMIT_DAYS.money}
        />
        <FacilityFilter reportKey="daily-collections" value={facility} onChange={setFacility} />
        <div>
          <Button type="submit" variant="primary" isDisabled={!valid}>
            Show report
          </Button>
        </div>
      </form>
      <ReportStates
        query={report}
        what="collections"
        isEmpty={(d) =>
          d.facilities.every((f) => f.paymentCount === 0 && f.total.refunded.cents === '0')
        }
        emptyText={`No payments on ${periodWords(shown.period)}.`}
      >
        {(d) => (
          <>
            <AsAt dataAsAt={d.dataAsAt} />
            {d.facilities.map((f) => (
              <FacilityCollections
                key={f.facilityId}
                facility={f}
                several={d.facilities.length > 1}
              />
            ))}
            {d.total === null ? null : (
              <section className={styles.section} aria-labelledby="all-total">
                <h2 id="all-total" className={styles.sectionHeading}>
                  All facilities
                </h2>
                <MethodTable totals={d.total} />
              </section>
            )}
          </>
        )}
      </ReportStates>
      {data === undefined ? null : (
        <>
          <ReportActions
            reportKey="daily-collections"
            sheet={sheet}
            printBody={{
              period: data.period,
              facilityIds: data.facilities.map((f) => f.facilityId),
              rowCount: data.facilities.reduce((n, f) => n + f.paymentCount, 0),
            }}
            exportBody={shown}
            disabled={report.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="Daily collections"
            facility={facilityWords(data.facilities.map((f) => f.name))}
            period={periodWords(data.period)}
            dataAsAt={data.dataAsAt}
          >
            {data.facilities.map((f) => (
              <div key={f.facilityId}>
                {data.facilities.length > 1 ? <h2>{f.name}</h2> : null}
                <PrintTable>
                  <MethodRows totals={f} />
                </PrintTable>
              </div>
            ))}
          </PrintableReport>
        </>
      )}
    </div>
  );
}

function MethodRows({ totals }: { totals: { byMethod: Facility['byMethod']; total: Totals } }) {
  return (
    <>
      <thead>
        <tr>
          <th scope="col">Method</th>
          <th scope="col" data-numeric>
            Received
          </th>
          <th scope="col" data-numeric>
            Refunded
          </th>
          <th scope="col" data-numeric>
            Net
          </th>
        </tr>
      </thead>
      <tbody>
        {totals.byMethod.map((m) => (
          <tr key={m.method}>
            <td>{METHOD_LABELS[m.method]}</td>
            <td data-numeric>{money(m.received)}</td>
            <td data-numeric>{money(m.refunded)}</td>
            <td data-numeric>{money(m.net)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td data-numeric>{money(totals.total.received)}</td>
          <td data-numeric>{money(totals.total.refunded)}</td>
          <td data-numeric>{money(totals.total.net)}</td>
        </tr>
      </tfoot>
    </>
  );
}

function MethodTable({ totals }: { totals: { byMethod: Facility['byMethod']; total: Totals } }) {
  return (
    <TableScroll>
      <table className={styles.table} aria-label="Totals by method">
        <MethodRows totals={totals} />
      </table>
    </TableScroll>
  );
}

function FacilityCollections({ facility: f, several }: { facility: Facility; several: boolean }) {
  const id = `collections-${f.facilityId}`;
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionHeading}>
        {several ? f.name : 'By method'}
      </h2>
      <p className={styles.summary}>
        {`${String(f.paymentCount)} ${f.paymentCount === 1 ? 'payment' : 'payments'}, ${money(f.total.received)} received, ${money(f.total.refunded)} refunded, ${money(f.total.net)} net.`}
      </p>
      <MethodTable totals={f} />
      <h3 className={styles.sectionHeading}>By cashier and shift</h3>
      {f.shifts.length === 0 ? (
        <p className={styles.muted}>No shifts took payments in this period.</p>
      ) : (
        <TableScroll>
          <table className={styles.table} aria-label="By cashier and shift">
            <thead>
              <tr>
                <th scope="col">Shift</th>
                <th scope="col">Cashier</th>
                <th scope="col" data-numeric>
                  Received in period
                </th>
                <th scope="col" data-numeric>
                  Variance
                </th>
                <th scope="col">Check</th>
              </tr>
            </thead>
            <tbody>
              {f.shifts.map((s) => (
                <tr key={s.shiftId}>
                  <td>
                    <span className="wodi-id">{s.number}</span>
                    <span className={styles.secondLine}>
                      {`Opened ${formatInstant(s.openedAt)}${s.closedAt === null ? '' : `, closed ${formatInstant(s.closedAt)}`}`}
                    </span>
                  </td>
                  <td>{s.cashierName}</td>
                  <td data-numeric>{money(s.receivedInPeriod)}</td>
                  <td data-numeric>
                    {s.variance === null ? '' : money(s.variance)}
                    {s.varianceReason === null ? null : (
                      <span className={styles.secondLine}>{s.varianceReason}</span>
                    )}
                  </td>
                  <td>
                    {RECONCILIATION_WORDS[s.reconciliation]}
                    {s.differingMethods.length === 0 ? null : (
                      <span className={styles.secondLine}>
                        {s.differingMethods.map((m) => METHOD_LABELS[m]).join(', ')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {f.references.length === 0 ? null : (
        <>
          <h3 className={styles.sectionHeading}>M-Pesa, card and bank references</h3>
          <TableScroll>
            <table className={styles.table} aria-label="References">
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Receipt</th>
                  <th scope="col">Method</th>
                  <th scope="col">Reference</th>
                  <th scope="col" data-numeric>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {f.references.map((r, i) => (
                  <tr key={`${r.receiptNumber ?? ''}-${String(i)}`}>
                    <td>{formatInstant(r.receivedAt)}</td>
                    <td className="wodi-id">{r.receiptNumber ?? ''}</td>
                    <td>{METHOD_LABELS[r.method]}</td>
                    <td className="wodi-id">{r.reference ?? ''}</td>
                    <td data-numeric>{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          {f.referencesTruncated ? (
            <p className={styles.muted}>
              Only the first 1,000 are shown. Download CSV for all of them.
            </p>
          ) : null}
        </>
      )}
      {f.lateEntries.length === 0 ? null : (
        <>
          <h3 className={styles.sectionHeading}>Entered after the day ended</h3>
          <TableScroll>
            <table className={styles.table} aria-label="Entered after the day ended">
              <thead>
                <tr>
                  <th scope="col">Received</th>
                  <th scope="col">Entered</th>
                  <th scope="col">Receipt</th>
                  <th scope="col">Method</th>
                  <th scope="col" data-numeric>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {f.lateEntries.map((l, i) => (
                  <tr key={`${l.receiptNumber ?? ''}-${String(i)}`}>
                    <td>{formatInstant(l.receivedAt)}</td>
                    <td>{formatInstant(l.enteredAt)}</td>
                    <td className="wodi-id">{l.receiptNumber ?? ''}</td>
                    <td>{METHOD_LABELS[l.method]}</td>
                    <td data-numeric>{money(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </>
      )}
    </section>
  );
}
